// Used by: Home
import { useCallback, useEffect, useState } from "react";
import DatePickerInput from "../ui/DatePickerInput";
import FromToInput from "../ui/FromToInput";
import TimePickerInput from "../ui/TimePickerInput";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import useModal from "../../hooks/useModal";
import QuoteQueryPersonalDetails from "./QuoteQueryPersonalDetails";
import getClassName from "../../utilities/getClassName";
import { setMinDate, setMaxDate, setMinTime } from "../../utilities/date";
import { createInitialJourney } from "../../utilities/quoteQueryJets";
import parse from "date-fns/parse";
import format from "date-fns/format";

// Icons
import plusIcon from "../../assets/icons/add-leg.svg";
import closeCrossDark from "../../assets/icons/close-cross-dark-heavy.svg";
import exclamationIcon from "../../assets/icons/exclamation-mark.svg";
import TextInputAlt from "../ui/TextInputAlt";

const GROUP_CHARTER_JOURNEY_DEFAULTS = { journeyType: "group-charter" };

const QuoteQueryGroupCharter = () => {
    const [journeyCounter, setJourneyCounter] = useState(1);
    const [journeyData, setJourneyData] = useState(() => [createInitialJourney(1, GROUP_CHARTER_JOURNEY_DEFAULTS)]);
    const [validateForm, setValidateForm] = useState(false);
    const [formIsValid, setFormIsValid] = useState(false);
    const [showError, setShowError] = useState(false);

    const { modalVisible, openModal, closeModal } = useModal();

    const validateFormHandler = useCallback(() => {
        let errorCount = 0;
        journeyData.forEach((journey) => {
            if (!journey.flightFrom) errorCount++;
            if (!journey.flightTo) errorCount++;
            if (!journey.date) errorCount++;
            if (!journey.time) errorCount++;
            if (!journey.passengers) errorCount++;
        });
        setFormIsValid(errorCount === 0);
        if (errorCount === 0) setShowError(false);
    }, [journeyData]);

    useEffect(() => {
        validateFormHandler();
    }, [journeyData, validateForm, validateFormHandler]);

    const addJourneyHandler = () => {
        const lastJourney = journeyData[journeyData.length - 1];
        let newDate;
        if (lastJourney.date) {
            newDate = parse(lastJourney.date, "dd/MM/yyyy", new Date());
            newDate.setDate(newDate.getDate() + 3);
            newDate = format(newDate, "dd/MM/yyyy", { awareOfUnicodeTokens: true });
        }
        const newJourney = createInitialJourney(journeyCounter + 1, {
            ...GROUP_CHARTER_JOURNEY_DEFAULTS,
            flightFrom: lastJourney.flightTo,
            flightTo: lastJourney.flightFrom,
            date: newDate,
            time: lastJourney.time,
            luggage: lastJourney.luggage,
            passengers: lastJourney.passengers,
        });
        setJourneyCounter((c) => c + 1);
        setJourneyData((prev) => [...prev, newJourney]);
        setValidateForm(false);
        setFormIsValid(false);
        setShowError(false);
    };

    const removeJourneyHandler = (index) => {
        setJourneyData((prev) => prev.filter((_, i) => i !== index));
    };

    const resetJourneyHandler = () => {
        setValidateForm(false);
        setFormIsValid(false);
        setShowError(false);
        setJourneyCounter(1);
        setJourneyData([createInitialJourney(1, GROUP_CHARTER_JOURNEY_DEFAULTS)]);
    };

    const submitHandler = (e) => {
        e.preventDefault();
        setValidateForm(true);
        if (formIsValid) openModal();
        else setShowError(true);
    };

    const updateInputHandler = (index, key, value) => {
        setJourneyData((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [key]: value };
            return next;
        });
    };

    return (
        <>
            <div className="quote-query">
                <form onSubmit={submitHandler}>
                    {journeyData.map((journey, i) => {
                        return (
                            <div
                                key={journey.id}
                                className={getClassName([
                                    "quote-query__inputs",
                                    journeyData.length > 1 && "quote-query__inputs--with-delete",
                                ])}
                            >
                                <div className="quote-query__inputs__column">
                                    <FromToInput
                                        value={(fromValue) => updateInputHandler(i, "flightFrom", fromValue)}
                                        defaultValue={journeyData[i]["flightFrom"]}
                                        direction="from"
                                        label="From"
                                        required={true}
                                        errorOnBlur={false}
                                        altErrorStyle={true}
                                        validateForm={validateForm}
                                    />
                                </div>
                                <div className="quote-query__inputs__column">
                                    <FromToInput
                                        value={(toValue) => updateInputHandler(i, "flightTo", toValue)}
                                        defaultValue={journeyData[i]["flightTo"]}
                                        direction="to"
                                        label="To"
                                        required={true}
                                        errorOnBlur={false}
                                        validateForm={validateForm}
                                    />
                                </div>
                                <div className="quote-query__inputs__column quote-query__inputs__column--medium-width">
                                    <DatePickerInput
                                        value={(dateValue) => updateInputHandler(i, "date", dateValue)}
                                        defaultValue={journeyData[i]["date"]}
                                        label="Date"
                                        required={true}
                                        errorOnBlur={false}
                                        validateForm={validateForm}
                                        minDate={setMinDate(i, journeyData)}
                                        maxDate={setMaxDate(i, journeyData)}
                                    />
                                </div>
                                <div className="quote-query__inputs__column quote-query__inputs__column--medium-width">
                                    <TimePickerInput
                                        value={(timeValue) => updateInputHandler(i, "time", timeValue)}
                                        defaultValue={journeyData[i]["time"]}
                                        label="Time"
                                        required={true}
                                        errorOnBlur={false}
                                        validateForm={validateForm}
                                        minTime={setMinTime(i, journeyData, false)}
                                    />
                                </div>
                                <div className="quote-query__inputs__column quote-query__inputs__column--shared">
                                    <TextInputAlt
                                        value={(fromValue) => updateInputHandler(i, "passengers", fromValue)}
                                        placeholder="Passengers"
                                        defaultValue={journeyData[i]["passengers"]}
                                        required={true}
                                        icon="person"
                                        maxLength={20}
                                        validateForm={validateForm}
                                    />
                                </div>

                                {journeyData.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeJourneyHandler(i)}
                                        className="quote-query__delete-button"
                                    >
                                        <img src={closeCrossDark} alt="" />
                                        Delete
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </form>

                <div className="quote-query__actions">
                    <button className="quote-query__actions__add-leg-button" onClick={addJourneyHandler}>
                        <img alt="" src={plusIcon} /> Add leg / return
                    </button>

                    {showError && (
                        <div className="quote-query__error">
                            <img src={exclamationIcon} alt="" />
                            Please complete your journey details.
                        </div>
                    )}

                    <div className="quote-query__submit-container">
                        <Button onClick={submitHandler} classNames={["button--full"]}>
                            Get a Quote
                        </Button>
                    </div>
                </div>
            </div>

            <Modal modalVisible={modalVisible} closeCrossColour="white" closeModal={closeModal}>
                <QuoteQueryPersonalDetails
                    journeyType="group-charter"
                    journeyData={journeyData}
                    resetJourney={resetJourneyHandler}
                    journeyIsValid={formIsValid}
                    closeModal={closeModal}
                />
            </Modal>
        </>
    );
};

export default QuoteQueryGroupCharter;
